import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import useHorizontalDrag from "../../utils/useHorizontalDrag";

const wrap = (value, count) => (value + count) % count;

export default function useShowcaseSlider(items, reducedMotion) {
  const count = items?.length || 0;
  const [track, setTrack] = useState(null);
  const [index, setIndex] = useState(0);
  const [interacting, setInteracting] = useState(false);
  const current = useRef(0);
  const destination = useRef(null);
  const settleTimer = useRef(null);
  const dragging = useRef(false);

  const positionFor = useCallback(
    (slot) => {
      const slide = track?.children[slot];
      return slide
        ? slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2
        : 0;
    },
    [track]
  );

  const goTo = useCallback(
    (slot, smooth = false) => {
      if (!track) return;
      const left = positionFor(slot);
      if (smooth && !reducedMotion)
        track.scrollTo({ left, behavior: "smooth" });
      else track.scrollLeft = left;
    },
    [track, positionFor, reducedMotion]
  );

  const select = useCallback(
    (value) => {
      if (count < 2) return;
      const next = wrap(value, count);
      // Adjacent end clones let the last/first transition move one card in the
      // requested direction. Rebase to the identical real card after settling.
      const candidates = [next + 1];
      if (next === 0) candidates.push(count + 1);
      if (next === count - 1) candidates.push(0);
      // Also take the shortest path when the user reverses a wrap mid-flight.
      const left = track?.scrollLeft || 0;
      const direction = (value < current.current ? -1 : 1) *
        (track && getComputedStyle(track).direction === "rtl" ? -1 : 1);
      const slot = candidates.reduce((closest, candidate) => {
        const delta = positionFor(candidate) - left;
        const closestDistance = Math.abs(positionFor(closest) - left);
        // Two-banner loops have equally close copies on both sides. Preserve
        // the requested direction, including fractional layout rounding.
        if (Math.abs(Math.abs(delta) - closestDistance) <= 1)
          return delta * direction > 0 ? candidate : closest;
        return Math.abs(delta) < closestDistance ? candidate : closest;
      });
      current.current = next;
      destination.current = slot;
      setIndex(next);
      goTo(slot, true);
    },
    [count, goTo, positionFor, track]
  );

  useLayoutEffect(() => {
    clearTimeout(settleTimer.current);
    destination.current = null;
    current.current = 0;
    dragging.current = false;
    setIndex(0);
    setInteracting(false);
    if (track) track.scrollLeft = positionFor(count > 1 ? 1 : 0);
  }, [items, track, count, positionFor]);

  useEffect(() => {
    if (!track || count < 2) return;
    const nearestSlot = () => {
      let nearest = 0;
      let distance = Infinity;
      for (let slot = 0; slot < track.children.length; slot++) {
        const delta = Math.abs(track.scrollLeft - positionFor(slot));
        if (delta < distance) {
          nearest = slot;
          distance = delta;
        }
      }
      return nearest;
    };
    const update = (slot) => {
      current.current = wrap(slot - 1, count);
      setIndex(current.current);
    };
    const settle = () => {
      if (dragging.current) return;
      clearTimeout(settleTimer.current);
      const slot = nearestSlot();
      // An interrupted animation may emit scrollend before reaching its target.
      if (
        destination.current !== null &&
        Math.abs(track.scrollLeft - positionFor(destination.current)) > 2
      )
        return;
      destination.current = null;
      update(slot);
      if (slot === 0) goTo(count);
      else if (slot === count + 1) goTo(1);
      setInteracting(false);
    };
    const onScroll = () => {
      setInteracting(true);
      if (destination.current === null) update(nearestSlot());
      clearTimeout(settleTimer.current);
      settleTimer.current = setTimeout(settle, 180);
    };
    const start = (event) => {
      if (
        event.type === "wheel" &&
        (event.ctrlKey || Math.abs(event.deltaX) <= Math.abs(event.deltaY))
      )
        return;
      destination.current = null;
      if (event.type === "pointerdown") dragging.current = true;
      setInteracting(true);
      clearTimeout(settleTimer.current);
      settleTimer.current = setTimeout(settle, 180);
    };
    const end = () => {
      dragging.current = false;
      clearTimeout(settleTimer.current);
      settleTimer.current = setTimeout(settle, 180);
    };
    const resize = () => {
      destination.current = null;
      goTo(current.current + 1);
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    track.addEventListener("scrollend", settle);
    track.addEventListener("wheel", start, { passive: true });
    track.addEventListener("pointerdown", start, { passive: true });
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    window.addEventListener("blur", end);
    track.addEventListener("lostpointercapture", end);
    window.addEventListener("resize", resize);
    const observer =
      typeof ResizeObserver === "function" ? new ResizeObserver(resize) : null;
    if (observer) observer.observe(track);
    return () => {
      clearTimeout(settleTimer.current);
      track.removeEventListener("scroll", onScroll);
      track.removeEventListener("scrollend", settle);
      track.removeEventListener("wheel", start);
      track.removeEventListener("pointerdown", start);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      window.removeEventListener("blur", end);
      track.removeEventListener("lostpointercapture", end);
      window.removeEventListener("resize", resize);
      if (observer) observer.disconnect();
    };
  }, [track, count, positionFor, goTo]);

  useHorizontalDrag(track);
  return {
    trackRef: setTrack,
    index: count ? index % count : 0,
    select,
    interacting
  };
}
